<template>
	<page :title="property.full_address" :breadcrumbs="breadcrumbs">
		<template #actions>
			<v-menu
				v-model="statusMenu"
				offset-y
				:nudge-width="200"
				:close-on-content-click="false">
				<template #activator="{ on, attrs }">
					<fieldset>
						<legend>Property Status</legend>
						<v-btn
							:color="property.status.color"
							dark
							large
							min-width="200"
							elevation="0"
							v-bind="attrs"
							v-on="on">
							{{ property.status.name }}
						</v-btn>
					</fieldset>
				</template>
				<v-card v-if="length(statuses)">
					<v-card-text>
						<v-autocomplete
							v-model="statusId"
							:items="statuses"
							placeholder="Change Status"
							item-text="name"
							item-value="id"
							filled
							outlined
							clearable
							hide-details>
							<template #selection="data">
								<v-icon left :color="data.item.color">mdi-circle</v-icon>
								<span>{{ data.item.name }}</span>
							</template>
							<template #item="data">
								<v-icon left :color="data.item.color">mdi-circle</v-icon>
								<span>{{ data.item.name }}</span>
							</template>
						</v-autocomplete>
						<v-btn color="primary"
							class="mt-3"
							:disabled="! statusId || statusId === project.status.id"
							@click="updateStatus">
							Change Status
						</v-btn>
					</v-card-text>
				</v-card>
			</v-menu>
		</template>
		<v-row class="mb-2">
			<v-col class="pt-0 mt-n3">
				<Link
					class="mr-2 v-btn v-btn--is-elevated v-btn--has-bg v-btn--rounded theme--light v-size--small secondary"
					:href="route('projects.show', project)">
					<v-icon left color="white">mdi-arrow-left-bold</v-icon>
					Back to Project
				</Link>
				<v-dialog v-model="dialogEdit" transition="scroll-x-reverse-transition" max-width="1100">
					<template #activator="{ on, attrs }">
						<v-btn
							color="success"
							small
							rounded
							:model="property"
							:model-id="property.id"
							v-bind="attrs"
							v-on="on">
							<v-icon left>mdi-pencil</v-icon>
							Edit Property
						</v-btn>
					</template>
					<template #default>
						<v-card>
							<v-card-title>
								<div>Edit Property</div>
								<v-icon right class="ml-auto theme--dark" @click="dialogEdit = false">mdi-close</v-icon>
							</v-card-title>
							<v-divider class="mb-5" />
							<v-card-text>
								<property-form :property="property" @success="dialogEdit = false"></property-form>
							</v-card-text>
						</v-card>
					</template>
				</v-dialog>
			</v-col>
		</v-row>
		<v-sheet rounded class="mb-6 pa-3 pb-1" elevation="15">
			<property-summary :property="property"></property-summary>
		</v-sheet>
		<v-spacer class="py-2" />
		<v-sheet rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<h3 class="white--text">Current Site Analysis</h3>
					<v-spacer />
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<v-row class="pt-4">
						<v-col cols="4">
							<v-select
								v-model="form.current_site_analysis.building_type"
								:items="buildingTypes"
								persistent-placeholder
								:error="hasErrors(form, 'current_site_analysis.building_type')"
								:hide-details="! hasErrors(form, 'current_site_analysis.building_type')"
								:error-messages="errorMsg(form, 'current_site_analysis.building_type')"
								label="Building Type"
								outlined
								dense
								class="mb-4">
								<template #selection="data">
									{{ data.item | capitalize }}
								</template>
								<template #item="data">
									{{ data.item | capitalize }}
								</template>
							</v-select>
						</v-col>
						<v-col cols="4">
							<v-text-field
								v-model="form.current_site_analysis.building_sq_feet"
								label="Building Sq Ft"
								name="current_site_analysis.building_sq_feet"
								persistent-placeholder
								:error="hasErrors(form, 'current_site_analysis.building_sq_feet')"
								:hide-details="! hasErrors(form, 'current_site_analysis.building_sq_feet')"
								:error-messages="errorMsg(form, 'current_site_analysis.building_sq_feet')"
								outlined
								dense
								class="mb-4" />
						</v-col>
						<v-col cols="4">
							<v-text-field
								v-model="form.current_site_analysis.lot_size"
								label="Lot Size"
								name="current_site_analysis.lot_size"
								persistent-placeholder
								:error="hasErrors(form, 'current_site_analysis.lot_size')"
								:hide-details="! hasErrors(form, 'current_site_analysis.lot_size')"
								:error-messages="errorMsg(form, 'current_site_analysis.lot_size')"
								persistent-hint
								outlined
								dense
								class="mb-4" />
						</v-col>
					</v-row>
					<v-row class="grey lighten-5 pt-5 pb-0 mb-4 form-section">
						<v-col cols="4">
							<v-text-field
								v-model="form.current_site_analysis.number_of_buildings"
								label="Number of Buildings"
								name="current_site_analysis.number_of_buildings"
								persistent-placeholder
								:error="hasErrors(form, 'current_site_analysis.number_of_buildings')"
								:error-messages="errorMsg(form, 'current_site_analysis.number_of_buildings')"
								hint="< 10 Spell the # | > 9 Enter the digit"
								persistent-hint
								outlined
								dense
								class="mb-4" />
						</v-col>
						<v-col cols="4">
							<v-text-field
								v-model="form.current_site_analysis.number_of_units"
								label="Number of Units"
								name="current_site_analysis.number_of_units"
								persistent-placeholder
								:error="hasErrors(form, 'current_site_analysis.number_of_units')"
								:error-messages="errorMsg(form, 'current_site_analysis.number_of_units')"
								hint="< 10 Spell the # | > 9 Enter the digit"
								persistent-hint
								outlined
								dense
								class="mb-4" />
						</v-col>
						<v-col cols="4">
							<v-text-field
								v-model="form.current_site_analysis.date_constructed"
								label="Date Constructed"
								name="current_site_analysis.date_constructed"
								persistent-placeholder
								:error="hasErrors(form, 'current_site_analysis.date_constructed')"
								:error-messages="errorMsg(form, 'current_site_analysis.date_constructed')"
								hint="Year Only ex: 1986"
								persistent-hint
								dense
								outlined />
						</v-col>
					</v-row>
					<v-row class="pt-0">
						<v-col cols="4">
							<v-text-field
								v-model="form.current_site_analysis.latitude"
								label="Latitude"
								name="current_site_analysis.latitude"
								persistent-placeholder
								:error="hasErrors(form, 'current_site_analysis.latitude')"
								:hide-details="! hasErrors(form, 'current_site_analysis.latitude')"
								:error-messages="errorMsg(form, 'current_site_analysis.latitude')"
								outlined
								dense
								class="mb-4" />
						</v-col>
						<v-col cols="4">
							<v-text-field
								v-model="form.current_site_analysis.longitude"
								label="Longitude"
								name="current_site_analysis.longitude"
								persistent-placeholder
								:error="hasErrors(form, 'current_site_analysis.longitude')"
								:hide-details="! hasErrors(form, 'current_site_analysis.longitude')"
								:error-messages="errorMsg(form, 'current_site_analysis.longitude')"
								outlined
								dense
								class="mb-4" />
						</v-col>
					</v-row>
					<v-row class="grey lighten-5 pt-5 pb-4 form-section">
						<v-col cols="6">
							<v-select
								v-model="form.current_site_analysis.drinking_water_source"
								persistent-placeholder
								:items="['municipal drinking water', 'on-Site drinking water well', 'Bell Oldow is not able to determine potable water supply (municipal or on-Site wells) or waste system (municipal sewer or on-Site septic).']"
								:error="hasErrors(form, 'current_site_analysis.drinking_water_source')"
								:hide-details="! hasErrors(form, 'current_site_analysis.drinking_water_source')"
								:error-messages="errorMsg(form, 'current_site_analysis.drinking_water_source')"
								label="Drinking Water Source"
								outlined
								dense
								class="mb-4">
								<template #selection="data">
									{{ data.item | capitalize }}
								</template>
								<template #item="data">
									{{ data.item | capitalize }}
								</template>
							</v-select>
						</v-col>
						<v-col cols="6">
							<v-select
								v-model="form.current_site_analysis.waste_system"
								persistent-placeholder
								:items="['municipal sewer', 'on-Site septic system']"
								:error="hasErrors(form, 'current_site_analysis.waste_system')"
								:hide-details="! hasErrors(form, 'current_site_analysis.waste_system')"
								:error-messages="errorMsg(form, 'current_site_analysis.waste_system')"
								label="Waste System"
								outlined
								dense
								class="mb-4">
								<template #selection="data">
									{{ data.item | capitalize }}
								</template>
								<template #item="data">
									{{ data.item | capitalize }}
								</template>
							</v-select>
						</v-col>
						<v-col cols="6" class="pt-0">
							<v-select
								v-model="form.current_site_analysis.heating_fuel"
								persistent-placeholder
								:items="['natural gas', 'fuel oil', 'electricity', 'natural gas and/or electric', 'Bell Oldow is not able to determine how the facility is heated.']"
								:error="hasErrors(form, 'current_site_analysis.heating_fuel')"
								:hide-details="! hasErrors(form, 'current_site_analysis.heating_fuel')"
								:error-messages="errorMsg(form, 'current_site_analysis.heating_fuel')"
								label="Heating Fuel"
								outlined
								dense>
								<template #selection="data">
									{{ data.item | capitalize }}
								</template>
								<template #item="data">
									{{ data.item | capitalize }}
								</template>
							</v-select>
						</v-col>
						<v-col cols="6" class="pt-0">
							<v-select
								v-model="form.current_site_analysis.flood_zone"
								persistent-placeholder
								:items="['A- High Risk', 'AE- High Risk', 'AH- High Risk', 'AO- High Risk', 'AR- High Risk', 'A99- High Risk', 'V- High Risk', 'VE- High Risk', 'B/X- Moderate/Minimal Risk', 'C/X- Moderate/Minimal Risk', 'D- Undetermined']"
								:error="hasErrors(form, 'current_site_analysis.flood_zone')"
								:hide-details="! hasErrors(form, 'current_site_analysis.flood_zone')"
								:error-messages="errorMsg(form, 'current_site_analysis.flood_zone')"
								label="Flood Zone"
								outlined
								dense>
								<template #selection="data">
									{{ data.item | capitalize }}
								</template>
								<template #item="data">
									{{ data.item | capitalize }}
								</template>
							</v-select>
						</v-col>
					</v-row>
					<v-row class="pt-3 pb-4">
						<v-col cols="12" class="pt-0">
							<div class="d-flex">
								<div class="mr-auto">
									<v-card-title class="pl-0 pb-4 section-title">
										Current Site Analysis
									</v-card-title>
								</div>
								<div>
									<v-btn
										text
										small
										color="primary"
										class="text-capitalize font-size-2"
										@click="generateCurrentSiteAnalysis">
										<v-icon left>mdi-arrow-down-bold</v-icon>
										Create Paragraph
									</v-btn>
								</div>
							</div>
							<canned-text-field
								v-model="form.current_site_analysis.current_site_analysis"
								field="current_site_analysis.current_site_analysis"
								field-name="current_site_analysis"
								:form="form"
								:is-editor="true"
								:has-canned-text="false" />
						</v-col>
					</v-row>
					<v-row class="grey lighten-5 pt-5 pb-4 form-section">
						<v-col cols="12" class="pt-0">
							<div class="d-flex">
								<div class="mr-auto">
									<v-card-title class="pl-0 pb-4 section-title">
										Drinking Water Source, Heating Fuel, Waste System
									</v-card-title>
								</div>
								<div>
									<v-btn
										text
										small
										color="primary"
										class="text-capitalize font-size-2"
										@click="generateWaterFuelWasteFloodZone">
										<v-icon left>mdi-arrow-down-bold</v-icon>
										Create Paragraph
									</v-btn>
								</div>
							</div>
							<canned-text-field
								v-model="form.current_site_analysis.current_site_floodzone"
								field="current_site_analysis.current_site_floodzone"
								field-name="current_site_floodzone"
								:form="form"
								:is-editor="true"
								:has-canned-text="false" />
						</v-col>
					</v-row>
					<canned-text-field
						v-model="form.current_site_analysis.current_site_usage"
						field="current_site_analysis.current_site_usage"
						field-name="current_site_usage"
						:form="form"
						label="Current Site Usage" />
					<canned-text-field
						v-model="form.current_site_analysis.current_hazmat"
						field="current_site_analysis.current_hazmat"
						field-name="current_hazmat"
						:form="form"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						label="Current HazMat" />
					<canned-text-field
						v-model="form.current_site_analysis.current_tank_info"
						field="current_site_analysis.current_tank_info"
						field-name="current_tank_info"
						:form="form"
						label="Current Tank Info" />
					<canned-text-field
						v-model="form.current_site_analysis.lbp_likelyhood"
						field="current_site_analysis.lbp_likelyhood"
						field-name="lbp_likelyhood"
						:form="form"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						label="LBP Likelihood" />
					<canned-text-field
						v-model="form.current_site_analysis.acm_likelyhood"
						field="current_site_analysis.acm_likelyhood"
						field-name="acm_likelyhood"
						:form="form"
						label="ACM Likelihood" />
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />
		<v-sheet v-if="form.historic_site_analysis" rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<h3 class="white--text">Historic Site Analysis</h3>
					<v-spacer />
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<canned-text-field
						v-model="form.historic_site_analysis.property_historic_development"
						field="historic_site_analysis.property_historic_development"
						field-name="property_historic_development"
						:form="form"
						label="Circumstances Before Current Site Facility" />
					<canned-text-field
						v-model="form.historic_site_analysis.historic_operation_in_current_facility"
						field="historic_site_analysis.historic_operation_in_current_facility"
						field-name="historic_operation_in_current_facility"
						:form="form"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						label="Historic Operations In Current Facility" />
					<canned-text-field
						v-model="form.historic_site_analysis.historical_site_use"
						field="historic_site_analysis.historical_site_use"
						field-name="historical_site_use"
						:form="form"
						label="Historic HazMat" />
					<v-row class="grey lighten-5 pt-5 pb-0 form-section" style="border-bottom: 0">
						<v-col cols="3">
							<v-text-field
								v-model="form.historic_site_analysis.phase_I_report_date"
								label="Phase I Report Date"
								name="historic_site_analysis.phase_I_report_date"
								persistent-placeholder
								:error="hasErrors(form, 'historic_site_analysis.phase_I_report_date')"
								:hide-details="! hasErrors(form, 'historic_site_analysis.phase_I_report_date')"
								:error-messages="errorMsg(form, 'historic_site_analysis.phase_I_report_date')"
								outlined
								dense
								class="mb-4" />
						</v-col>
						<v-col cols="3">
							<v-text-field
								v-model="form.historic_site_analysis.phase_I_consultant"
								label="Phase I Consultant"
								name="historic_site_analysis.phase_I_consultant"
								persistent-placeholder
								:error="hasErrors(form, 'historic_site_analysis.phase_I_consultant')"
								:hide-details="! hasErrors(form, 'historic_site_analysis.phase_I_consultant')"
								:error-messages="errorMsg(form, 'historic_site_analysis.phase_I_consultant')"
								outlined
								dense
								class="mb-4" />
						</v-col>
						<v-col cols="3">
							<v-text-field
								v-model="form.historic_site_analysis.phase_I_generated_for"
								label="Generated For"
								name="historic_site_analysis.phase_I_generated_for"
								persistent-placeholder
								:error="hasErrors(form, 'historic_site_analysis.phase_I_generated_for')"
								:hide-details="! hasErrors(form, 'historic_site_analysis.phase_I_generated_for')"
								:error-messages="errorMsg(form, 'historic_site_analysis.phase_I_generated_for')"
								outlined
								dense
								class="mb-4" />
						</v-col>
					</v-row>
					<canned-text-field
						v-model="form.historic_site_analysis.previous_environmental_investigation"
						field="historic_site_analysis.previous_environmental_investigation"
						field-name="previous_environmental_investigation"
						:form="form"
						label="Previous Phase I Environmental Investigation"
						row-class="grey lighten-5 pt-0 pb-4 bt-0 form-section" />
					<v-row class="pt-5">
						<v-col cols="3">
							<v-text-field
								v-model="form.historic_site_analysis.phase_II_report_date"
								label="Phase II Report Date"
								name="historic_site_analysis.phase_II_report_date"
								persistent-placeholder
								:error="hasErrors(form, 'historic_site_analysis.phase_II_report_date')"
								:hide-details="! hasErrors(form, 'historic_site_analysis.phase_II_report_date')"
								:error-messages="errorMsg(form, 'historic_site_analysis.phase_II_report_date')"
								outlined
								dense
								class="mb-4" />
						</v-col>
						<v-col cols="3">
							<v-text-field
								v-model="form.historic_site_analysis.phase_II_consultant"
								label="Phase II Consultant"
								name="historic_site_analysis.phase_II_consultant"
								persistent-placeholder
								:error="hasErrors(form, 'historic_site_analysis.phase_II_consultant')"
								:hide-details="! hasErrors(form, 'historic_site_analysis.phase_II_consultant')"
								:error-messages="errorMsg(form, 'historic_site_analysis.phase_II_consultant')"
								outlined
								dense
								class="mb-4" />
						</v-col>
						<v-col cols="3">
							<v-text-field
								v-model="form.historic_site_analysis.phase_II_generated_for"
								label="Generated For"
								name="historic_site_analysis.phase_II_generated_for"
								persistent-placeholder
								:error="hasErrors(form, 'historic_site_analysis.phase_II_generated_for')"
								:hide-details="! hasErrors(form, 'historic_site_analysis.phase_II_generated_for')"
								:error-messages="errorMsg(form, 'historic_site_analysis.phase_II_generated_for')"
								outlined
								dense
								class="mb-4" />
						</v-col>
					</v-row>
					<canned-text-field
						v-model="form.historic_site_analysis.previous_environmental_investigation_II"
						field="historic_site_analysis.previous_environmental_investigation_II"
						field-name="previous_environmental_investigation_II"
						:form="form"
						label="Previous Phase II Environmental Investigation"
						row-class="row pt-0 pb-5" />
					<canned-text-field
						v-model="form.historic_site_analysis.historic_tank_info"
						field="historic_site_analysis.historic_tank_info"
						field-name="historic_tank_info"
						:form="form"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						label="Historic Tank Information" />
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />
		<v-sheet v-if="form.environmental_database_review" rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<h3 class="white--text">Environmental Database Review</h3>
					<v-spacer />
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<canned-text-field
						v-model="form.environmental_database_review.site_environmental_database_review"
						field="environmental_database_review.site_environmental_database_review"
						field-name="site_environmental_database_review"
						:form="form"
						label="Site Environmental Database Review" />
					<canned-text-field
						v-model="form.current_site_analysis.current_condition_general"
						field="current_site_analysis.current_condition_general"
						field-name="current_condition_general"
						:form="form"
						label="Site Analysis Conclusion"
						row-class="grey lighten-5 pt-5 pb-4 form-section" />
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />
		<v-sheet rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<div class="d-flex" style="width: 100%">
						<div class="mr-auto">
							<h3 class="white--text">PFAS</h3>
						</div>
					</div>
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<canned-text-field
						v-model="form.environmental_database_review.pfas"
						field="environmental_database_review.pfas"
						field-name="pfas"
						:form="form"
						label="PFAS" />
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />
		<v-sheet rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<div class="d-flex" style="width: 100%">
						<div class="mr-auto">
							<h3 class="white--text">Other Environmental Concerns</h3>
						</div>
					</div>
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<canned-text-field
						v-model="form.environmental_database_review.other_environmental_concerns"
						field="environmental_database_review.other_environmental_concerns"
						field-name="other_environmental_concerns"
						:form="form"
						label="Other Environmental Concerns" />
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />
		<v-sheet rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<div class="d-flex" style="width: 100%">
						<div class="mr-auto">
							<h3 class="white--text">Environmental Risk Factors</h3>
						</div>
						<div class="align-content-end">
							<a
								class="white--text text-capitalize font-size-2 mr-3"
								@click="generateFloodZone">
								<v-icon left color="white">mdi-arrow-down-bold</v-icon>
								<small>Create Paragraph</small>
							</a>
						</div>
					</div>
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<canned-text-field
						v-model="form.environmental_database_review.risk_factors_review"
						field="environmental_database_review.risk_factors_review"
						field-name="risk_factors_review"
						:form="form"
						label="Risk Factors Review" />
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />
		<v-sheet v-if="form.off_site_analysis" rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<h3 class="white--text">Off Site Analysis</h3>
					<v-spacer />
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<v-row class="pt-4">
						<v-col cols="12" lg="6">
							<canned-text-field
								v-model="form.off_site_analysis.north_abutter"
								field="off_site_analysis.north_abutter"
								field-name="north_abutter"
								:form="form"
								label="North Abutter" />
						</v-col>
						<v-col cols="12" lg="6">
							<canned-text-field
								v-model="form.off_site_analysis.south_abutter"
								field="off_site_analysis.south_abutter"
								field-name="south_abutter"
								:form="form"
								label="South Abutter" />
						</v-col>
						<v-col cols="12" lg="6">
							<canned-text-field
								v-model="form.off_site_analysis.east_abutter"
								field="off_site_analysis.east_abutter"
								field-name="east_abutter"
								:form="form"
								label="East Abutter" />
						</v-col>
						<v-col cols="12" lg="6">
							<canned-text-field
								v-model="form.off_site_analysis.west_abutter"
								field="off_site_analysis.west_abutter"
								field-name="west_abutter"
								:form="form"
								label="West Abutter" />
						</v-col>
					</v-row>
					<canned-text-field
						v-model="form.off_site_analysis.current_use_of_abutting_properties"
						field="off_site_analysis.current_use_of_abutting_properties"
						field-name="current_use_of_abutting_properties"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						:form="form"
						label="Current use of abutting properties" />
					<canned-text-field
						v-model="form.off_site_analysis.historical_use_of_abutting_properties"
						field="off_site_analysis.historical_use_of_abutting_properties"
						field-name="historical_use_of_abutting_properties"
						:form="form"
						label="Historical use of abutting properties" />
					<canned-text-field
						v-model="form.environmental_database_review.offsite_environmental_database_review"
						field="environmental_database_review.offsite_environmental_database_review"
						field-name="offsite_environmental_database_review"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						:form="form"
						label="Off Site Environmental Database Review" />
					<v-row class="pt-4">
						<v-col>
							<div class="d-flex align-center">
								<div class="font-size-3 tnm-label">Distance between the Site and the nearest hazardous material release property</div>
								<div class="pl-4 pr-4 flex-grow-1">
									<v-text-field
										v-model="form.environmental_database_review.miles_from_site_of_nearest_haz_release_property"
										name="environmental_database_review.miles_from_site_of_nearest_haz_release_property"
										persistent-placeholder
										:error="hasErrors(form, 'environmental_database_review.miles_from_site_of_nearest_haz_release_property')"
										:hide-details="! hasErrors(form, 'environmental_database_review.miles_from_site_of_nearest_haz_release_property')"
										:error-messages="errorMsg(form, 'environmental_database_review.miles_from_site_of_nearest_haz_release_property')"
										outlined
										dense
										class="mb-4" />
								</div>
								<div>in miles, ex. 0.5</div>
							</div>
						</v-col>
					</v-row>
					<canned-text-field
						v-model="form.off_site_analysis.off_site_analysis_general"
						field="off_site_analysis.off_site_analysis_general"
						field-name="off_site_analysis_general"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						:form="form"
						label="Off Site Analysis Conclusion" />
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />
		<v-sheet rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<h3 class="white--text">Executive Summary</h3>
					<v-spacer />
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<v-row>
						<v-col>
							<div class="d-flex align-center">
								<div class="font-size-3 tnm-label">Is there known/documented contamination on Site?</div>
								<div class="pl-4 pr-4 flex-grow-1">
									<v-radio-group v-model="form.neb_summary.known_contamination" row>
										<v-radio label="Yes" value="Yes"></v-radio>
										<v-radio label="No" value="No"></v-radio>
									</v-radio-group>
								</div>
							</div>
						</v-col>
					</v-row>
					<canned-text-field
						v-model="form.neb_summary.known_contamination_reason"
						field="neb_summary.known_contamination_reason"
						field-name="known_contamination_reason"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						:form="form"
						label="Known On Site Contamination Description" />
					<canned-text-field
						v-model="form.neb_summary.likelyhood_of_contamination_reason"
						field="neb_summary.likelyhood_of_contamination_reason"
						field-name="likelyhood_of_contamination_reason"
						:form="form"
						label="Likelihood of Contamination Reason" />
					<canned-text-field
						v-model="form.neb_summary.likely_nature_of_potential_contamination"
						field="neb_summary.likely_nature_of_potential_contamination"
						field-name="likely_nature_of_potential_contamination"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						:form="form"
						label="Likely Nature of Potential Contamination" />
					<v-row>
						<v-col>
							<div class="d-flex align-center">
								<div class="font-size-3 tnm-label">Public Remediation Funding</div>
								<div class="pl-4 pr-4 flex-grow-1">
									<v-radio-group v-model="form.neb_summary.public_remediation_funding" row>
										<v-radio label="Yes" value="Yes"></v-radio>
										<v-radio label="No" value="No"></v-radio>
										<v-radio label="N/A" value="N/A"></v-radio>
									</v-radio-group>
								</div>
							</div>
						</v-col>
					</v-row>
					<canned-text-field
						v-model="form.executive_summary.executive_summary_general"
						field="executive_summary.executive_summary_general"
						field-name="executive_summary_general"
						row-class="grey lighten-5 pt-5 pb-4 form-section"
						:form="form"
						label="Executive Summary General" />
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />

		<v-sheet rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<h3 class="white--text">Master Servicing</h3>
					<v-spacer />
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<template v-for="(issue, index) in form.master_servicing.issues">
						<v-row v-if="index >= 1 && form.master_servicing['issues'][index]['show']" :key="`remove-master-servicing-` + index" style="margin-left: -20px; margin-right: -20px;" class="mb-2">
							<v-col class="amber lighten-5 pr-5 pl-5">
								<v-btn elevation="0"
									small
									color="brown lighten-4"
									:disabled="!!form.master_servicing['issues'][index].master_servicing_issue"
									@click="hideIssue('master_servicing', index)">
									<span>Hide Issue # {{ index + 1 }}</span>
								</v-btn>
							</v-col>
						</v-row>

						<canned-text-field
							v-if="form.master_servicing['issues'][index]['show']"
							:key="index + `master_servicing_issue`"
							v-model="form.master_servicing['issues'][index].master_servicing_issue"
							field="master_servicing.master_servicing_issue"
							canned-text-name="master_servicing_issue"
							:field-name="`master_servicing_issue_` + index"
							:form="form"
							:label="`Master Servicing Issue #` + (index + 1)" />
						<canned-text-field
							v-if="form.master_servicing['issues'][index]['show']"
							:key="index + `master_servicing_action`"
							v-model="form.master_servicing['issues'][index].master_servicing_action"
							field="master_servicing.master_servicing_action"
							:field-name="`master_servicing_action_` + index"
							canned-text-name="master_servicing_action"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							:label="`Master Servicing Action #` + (index + 1)" />
						<canned-text-field
							v-if="form.master_servicing['issues'][index]['show']"
							:key="index + `master_servicing_cost_from`"
							v-model="form.master_servicing['issues'][index].master_servicing_cost_from"
							field="master_servicing.master_servicing_cost_from"
							:field-name="`master_servicing_cost_from_` + index"
							canned-text-name="master_servicing_cost_from"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pt-3 pb-0"
							:label="`Cost From #` + (index + 1)" />
						<canned-text-field
							v-if="form.master_servicing['issues'][index]['show']"
							:key="index + `master_servicing_cost_to`"
							v-model="form.master_servicing['issues'][index].master_servicing_cost_to"
							field="master_servicing.master_servicing_cost_to"
							:field-name="`master_servicing_cost_to_` + index"
							canned-text-name="master_servicing_cost_to"
							field-type="cost"
							:form="form"
							:is-editor="false"
							:label="`Cost To #` + (index + 1)" />
						<canned-text-field
							v-if="form.master_servicing['issues'][index]['show']"
							:key="index + `master_servicing_time_to_resolve`"
							v-model="form.master_servicing['issues'][index].master_servicing_time_to_resolve"
							field="master_servicing.master_servicing_time_to_resolve"
							:field-name="`master_servicing_time_to_resolve_` + index"
							canned-text-name="master_servicing_time_to_resolve"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							:label="`Master Servicing Time To Resolve #` + (index + 1)" />
					</template>

					<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
						<v-col class="amber lighten-5 pr-5 pl-5">
							<v-btn elevation="0"
								small
								color="primary"
								@click="addIssue('master_servicing', ['master_servicing_issue', 'master_servicing_action', 'master_servicing_cost_from', 'master_servicing_cost_to', 'master_servicing_time_to_resolve'])">
								<span>Add Another Issue</span>
							</v-btn>
						</v-col>
					</v-row>

					<canned-text-field
						key="master_servicing_general"
						v-model="form.master_servicing.master_servicing_general"
						:form="form"
						field="master_servicing_general"
						field-name="master_servicing_general"
						canned-text-name="master_servicing_general"
						label="Master Servicing General" />
				</v-card-text>
			</v-card>
		</v-sheet>

		<v-spacer class="py-5" />
		<v-sheet rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<h3 class="white--text">Special Servicing</h3>
					<v-spacer />
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<canned-text-field
						v-model="form.special_servicing.sp_issue_1"
						field="special_servicing.sp_issue_1"
						field-name="sp_issue_1"
						:form="form"
						label="Special Servicing Issue #1" />
					<canned-text-field
						v-model="form.special_servicing.sp_action_1"
						field="special_servicing.sp_action_1"
						field-name="sp_action_1"
						row-class="grey lighten-5 pt-3 pb-4 form-section"
						:form="form"
						label="Special Servicing Action #1" />
					<canned-text-field
						v-model="form.special_servicing.sp_cost_from_1"
						field="special_servicing.sp_cost_from_1"
						field-name="sp_cost_from_1"
						field-type="cost"
						:form="form"
						:is-editor="false"
						row-class="pt-3 pb-0 pl-7"
						label="Cost From" />
					<canned-text-field
						v-model="form.special_servicing.sp_cost_to_1"
						field="special_servicing.sp_cost_to_1"
						field-name="sp_cost_to_1"
						field-type="cost"
						:form="form"
						:is-editor="false"
						row-class="pl-7"
						label="Cost To" />
					<canned-text-field
						v-model="form.special_servicing.sp_time_to_resolve_1"
						field="special_servicing.sp_time_to_resolve_1"
						field-name="sp_time_to_resolve_1"
						row-class="grey lighten-5 pt-3 pb-4 form-section"
						:form="form"
						label="Special Servicing Time To Resolve #1" />
					<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
						<v-col class="amber lighten-5 pr-5 pl-5">
							<v-btn elevation="0"
								small
								:color="!hasSpecialServicing2 ? 'primary' : 'brown lighten-4'"
								@click="showSpecialServicing2 = !showSpecialServicing2">
								<span v-if="!hasSpecialServicing2">Add Another Issue</span>
								<span v-else>Hide Issue #2</span>
							</v-btn>
						</v-col>
					</v-row>
					<div v-if="hasSpecialServicing2">
						<canned-text-field
							v-model="form.special_servicing.sp_issue_2"
							field="special_servicing.sp_issue_2"
							field-name="sp_issue_2"
							canned-text-name="sp_issue_1"
							:form="form"
							label="Special Servicing Issue #2" />
						<canned-text-field
							v-model="form.special_servicing.sp_action_2"
							field="special_servicing.sp_action_2"
							field-name="sp_action_2"
							canned-text-name="sp_action_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="Special Servicing Action #2" />
						<canned-text-field
							v-model="form.special_servicing.sp_cost_from_2"
							field="special_servicing.sp_cost_from_2"
							field-name="sp_cost_from_2"
							canned-text-name="sp_cost_from_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pt-3 pb-0 pl-7"
							label="Cost From" />
						<canned-text-field
							v-model="form.special_servicing.sp_cost_to_2"
							field="special_servicing.sp_cost_to_2"
							field-name="sp_cost_to_2"
							canned-text-name="sp_cost_to_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pl-7"
							label="Cost To" />
						<canned-text-field
							v-model="form.special_servicing.sp_time_to_resolve_2"
							field="special_servicing.sp_time_to_resolve_2"
							field-name="sp_time_to_resolve_2"
							canned-text-name="sp_time_to_resolve_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="Special Servicing Time To Resolve #2" />
						<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
							<v-col class="amber lighten-5 pr-5 pl-5">
								<v-btn elevation="0"
									small
									:color="!hasSpecialServicing3 ? 'primary' : 'brown lighten-4'"
									@click="showSpecialServicing3 = !showSpecialServicing3">
									<span v-if="!hasSpecialServicing3">Add Another Issue</span>
									<span v-else>Hide Issue #3</span>
								</v-btn>
							</v-col>
						</v-row>
						<div v-if="hasSpecialServicing3">
							<canned-text-field
								v-model="form.special_servicing.sp_issue_3"
								field="special_servicing.sp_issue_3"
								field-name="sp_issue_3"
								canned-text-name="sp_issue_1"
								:form="form"
								label="Special Servicing Issue #3" />
							<canned-text-field
								v-model="form.special_servicing.sp_action_3"
								field="special_servicing.sp_action_3"
								field-name="sp_action_3"
								canned-text-name="sp_action_1"
								row-class="grey lighten-5 pt-3 pb-4 form-section"
								:form="form"
								label="Special Servicing Action #3" />
							<canned-text-field
								v-model="form.special_servicing.sp_cost_from_3"
								field="special_servicing.sp_cost_from_3"
								field-name="sp_cost_from_3"
								canned-text-name="sp_cost_from_1"
								field-type="cost"
								:form="form"
								:is-editor="false"
								row-class="pt-3 pb-0 pl-7"
								label="Cost From" />
							<canned-text-field
								v-model="form.special_servicing.sp_cost_to_3"
								field="special_servicing.sp_cost_to_3"
								field-name="sp_cost_to_3"
								canned-text-name="sp_cost_to_1"
								field-type="cost"
								:form="form"
								:is-editor="false"
								row-class="pl-7"
								label="Cost To" />
							<canned-text-field
								v-model="form.special_servicing.sp_time_to_resolve_3"
								field="special_servicing.sp_time_to_resolve_3"
								field-name="sp_time_to_resolve_3"
								canned-text-name="sp_time_to_resolve_1"
								row-class="grey lighten-5 pt-3 pb-4 form-section"
								:form="form"
								label="Special Servicing Time To Resolve #3" />
							<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
								<v-col class="amber lighten-5 pr-5 pl-5">
									<v-btn elevation="0"
										small
										:color="!hasSpecialServicing4 ? 'primary' : 'brown lighten-4'"
										@click="showSpecialServicing4 = !showSpecialServicing4">
										<span v-if="!hasSpecialServicing4">Add Another Issue</span>
										<span v-else>Hide Issue #4</span>
									</v-btn>
								</v-col>
							</v-row>
						</div>
						<div v-if="hasSpecialServicing4" class="mb-2">
							<canned-text-field
								v-model="form.special_servicing.sp_issue_4"
								field="special_servicing.sp_issue_4"
								field-name="sp_issue_4"
								canned-text-name="sp_issue_1"
								:form="form"
								label="Special Servicing Issue #4" />
							<canned-text-field
								v-model="form.special_servicing.sp_action_4"
								field="special_servicing.sp_action_4"
								field-name="sp_action_4"
								canned-text-name="sp_action_1"
								row-class="grey lighten-5 pt-3 pb-4 form-section"
								:form="form"
								label="Special Servicing Action #4" />
							<canned-text-field
								v-model="form.special_servicing.sp_cost_from_4"
								field="special_servicing.sp_cost_from_4"
								field-name="sp_cost_from_4"
								canned-text-name="sp_cost_from_1"
								field-type="cost"
								:form="form"
								:is-editor="false"
								row-class="pt-3 pb-0 pl-7"
								label="Cost From" />
							<canned-text-field
								v-model="form.special_servicing.sp_cost_to_4"
								field="special_servicing.sp_cost_to_4"
								field-name="sp_cost_to_4"
								canned-text-name="sp_cost_to_1"
								field-type="cost"
								:form="form"
								:is-editor="false"
								row-class="pl-7"
								label="Cost To" />
							<canned-text-field
								v-model="form.special_servicing.sp_time_to_resolve_4"
								field="special_servicing.sp_time_to_resolve_4"
								field-name="sp_time_to_resolve_4"
								canned-text-name="sp_time_to_resolve_1"
								row-class="grey lighten-5 pt-3 pb-4 form-section"
								:form="form"
								label="Special Servicing Time To Resolve #4" />
							<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
								<v-col class="amber lighten-5 pr-5 pl-5">
									<v-btn elevation="0"
										small
										:color="!hasSpecialServicing5 ? 'primary' : 'brown lighten-4'"
										@click="showSpecialServicing5 = !showSpecialServicing5">
										<span v-if="!hasSpecialServicing5">Add Another Issue</span>
										<span v-else>Hide Issue #5</span>
									</v-btn>
								</v-col>
							</v-row>
						</div>
						<div v-if="hasSpecialServicing5" class="mb-2">
							<canned-text-field
								v-model="form.special_servicing.sp_issue_5"
								field="special_servicing.sp_issue_5"
								field-name="sp_issue_5"
								canned-text-name="sp_issue_1"
								:form="form"
								label="Special Servicing Issue #5" />
							<canned-text-field
								v-model="form.special_servicing.sp_action_5"
								field="special_servicing.sp_action_5"
								field-name="sp_action_5"
								canned-text-name="sp_action_1"
								row-class="grey lighten-5 pt-3 pb-4 form-section"
								:form="form"
								label="Special Servicing Action #5" />
							<canned-text-field
								v-model="form.special_servicing.sp_cost_from_5"
								field="special_servicing.sp_cost_from_5"
								field-name="sp_cost_from_5"
								canned-text-name="sp_cost_from_1"
								field-type="cost"
								:form="form"
								:is-editor="false"
								row-class="pt-3 pb-0 pl-7"
								label="Cost From" />
							<canned-text-field
								v-model="form.special_servicing.sp_cost_to_5"
								field="special_servicing.sp_cost_to_5"
								field-name="sp_cost_to_5"
								canned-text-name="sp_cost_to_1"
								field-type="cost"
								:form="form"
								:is-editor="false"
								row-class="pl-7"
								label="Cost To" />
							<canned-text-field
								v-model="form.special_servicing.sp_time_to_resolve_5"
								field="special_servicing.sp_time_to_resolve_5"
								field-name="sp_time_to_resolve_5"
								canned-text-name="sp_time_to_resolve_1"
								row-class="grey lighten-5 pt-3 pb-4 form-section"
								:form="form"
								label="Special Servicing Time To Resolve #5" />
							<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
								<v-col class="amber lighten-5 pr-5 pl-5">
									<v-btn elevation="0"
										small
										:color="!hasSpecialServicing6 ? 'primary' : 'brown lighten-4'"
										@click="showSpecialServicing6 = !showSpecialServicing6">
										<span v-if="!hasSpecialServicing6">Add Another Issue</span>
										<span v-else>Hide Issue #6</span>
									</v-btn>
								</v-col>
							</v-row>
						</div>
						<div v-if="hasSpecialServicing6" class="mb-2">
							<canned-text-field
								v-model="form.special_servicing.sp_issue_6"
								field="special_servicing.sp_issue_6"
								field-name="sp_issue_6"
								canned-text-name="sp_issue_1"
								:form="form"
								label="Special Servicing Issue #6" />
							<canned-text-field
								v-model="form.special_servicing.sp_action_6"
								field="special_servicing.sp_action_6"
								field-name="sp_action_6"
								canned-text-name="sp_action_1"
								row-class="grey lighten-5 pt-3 pb-4 form-section"
								:form="form"
								label="Special Servicing Action #6" />
							<canned-text-field
								v-model="form.special_servicing.sp_cost_from_6"
								field="special_servicing.sp_cost_from_6"
								field-name="sp_cost_from_6"
								canned-text-name="sp_cost_from_1"
								field-type="cost"
								:form="form"
								:is-editor="false"
								row-class="pt-3 pb-0 pl-7"
								label="Cost From" />
							<canned-text-field
								v-model="form.special_servicing.sp_cost_to_6"
								field="special_servicing.sp_cost_to_6"
								field-name="sp_cost_to_6"
								canned-text-name="sp_cost_to_1"
								field-type="cost"
								:form="form"
								:is-editor="false"
								row-class="pl-7"
								label="Cost To" />
							<canned-text-field
								v-model="form.special_servicing.sp_time_to_resolve_6"
								field="special_servicing.sp_time_to_resolve_6"
								field-name="sp_time_to_resolve_6"
								canned-text-name="sp_time_to_resolve_1"
								row-class="grey lighten-5 pt-3 pb-4 form-section"
								:form="form"
								label="Special Servicing Time To Resolve #6" />
						</div>
						<canned-text-field
							v-model="form.special_servicing.special_servicing_general"
							field="special_servicing.special_servicing_general"
							field-name="special_servicing_general"
							:form="form"
							label="Special Servicing General" />
					</div>
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />
		<v-sheet rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<h3 class="white--text">Real Estate Owned</h3>
					<v-spacer />
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<canned-text-field
						v-model="form.reo.reo_issue_1"
						field="reo.reo_issue_1"
						field-name="reo_issue_1"
						:form="form"
						label="REO Issue #1" />
					<canned-text-field
						v-model="form.reo.reo_action_1"
						field="reo.reo_action_1"
						field-name="reo_action_1"
						row-class="grey lighten-5 pt-3 pb-4 form-section"
						:form="form"
						label="REO Action #1" />
					<canned-text-field
						v-model="form.reo.reo_cost_from_1"
						field="reo.reo_cost_from_1"
						field-name="reo_cost_from_1"
						field-type="cost"
						:form="form"
						:is-editor="false"
						row-class="pt-3 pb-0 pl-7"
						label="Cost From" />
					<canned-text-field
						v-model="form.reo.reo_cost_to_1"
						field="reo.reo_cost_to_1"
						field-name="reo_cost_to_1"
						field-type="cost"
						:form="form"
						:is-editor="false"
						row-class="pl-7"
						label="Cost To" />
					<canned-text-field
						v-model="form.reo.reo_time_to_resolve_1"
						field="reo.reo_time_to_resolve_1"
						field-name="reo_time_to_resolve_1"
						row-class="grey lighten-5 pt-3 pb-4 form-section"
						:form="form"
						label="REO Time To Resolve #1" />
					<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
						<v-col class="amber lighten-5 pr-5 pl-5">
							<v-btn elevation="0"
								small
								:color="!hasReoIssue2 ? 'primary' : 'brown lighten-4'"
								@click="showREOIssue2 = !showREOIssue2">
								<span v-if="!hasReoIssue2">Add Another Issue</span>
								<span v-else>Hide Issue #2</span>
							</v-btn>
						</v-col>
					</v-row>
					<div v-if="hasReoIssue2">
						<canned-text-field
							v-model="form.reo.reo_issue_2"
							field="reo.reo_issue_2"
							field-name="reo_issue_2"
							canned-text-name="reo_issue_1"
							:form="form"
							label="REO Issue #2" />
						<canned-text-field
							v-model="form.reo.reo_action_2"
							field="reo.reo_action_2"
							field-name="reo_action_2"
							canned-text-name="reo_action_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Action #2" />
						<canned-text-field
							v-model="form.reo.reo_cost_from_2"
							field="reo.reo_cost_from_2"
							field-name="reo_cost_from_2"
							canned-text-name="reo_cost_from_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pt-3 pb-0 pl-7"
							label="Cost From" />
						<canned-text-field
							v-model="form.reo.reo_cost_to_2"
							field="reo.reo_cost_to_2"
							field-name="reo_cost_to_2"
							canned-text-name="reo_cost_to_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pl-7"
							label="Cost To" />
						<canned-text-field
							v-model="form.reo.reo_time_to_resolve_2"
							field="reo.reo_time_to_resolve_2"
							field-name="reo_time_to_resolve_2"
							canned-text-name="reo_time_to_resolve_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Time To Resolve #2" />
						<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
							<v-col class="amber lighten-5 pr-5 pl-5">
								<v-btn elevation="0"
									small
									:color="!hasReoIssue3 ? 'primary' : 'brown lighten-4'"
									@click="showREOIssue3 = !showREOIssue3">
									<span v-if="!hasReoIssue3">Add Another Issue</span>
									<span v-else>Hide Issue #3</span>
								</v-btn>
							</v-col>
						</v-row>
					</div>
					<div v-if="hasReoIssue3">
						<canned-text-field
							v-model="form.reo.reo_issue_3"
							field="reo.reo_issue_3"
							field-name="reo_issue_3"
							canned-text-name="reo_issue_1"
							:form="form"
							label="REO Issue #3" />
						<canned-text-field
							v-model="form.reo.reo_action_3"
							field="reo.reo_action_3"
							field-name="reo_action_3"
							canned-text-name="reo_action_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Action #3" />
						<canned-text-field
							v-model="form.reo.reo_cost_from_3"
							field="reo.reo_cost_from_3"
							field-name="reo_cost_from_3"
							canned-text-name="reo_cost_from_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pt-3 pb-0 pl-7"
							label="Cost From" />
						<canned-text-field
							v-model="form.reo.reo_cost_to_3"
							field="reo.reo_cost_to_3"
							field-name="reo_cost_to_3"
							canned-text-name="reo_cost_to_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pl-7"
							label="Cost To" />
						<canned-text-field
							v-model="form.reo.reo_time_to_resolve_3"
							field="reo.reo_time_to_resolve_3"
							field-name="reo_time_to_resolve_3"
							canned-text-name="reo_time_to_resolve_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Time To Resolve #3" />
						<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
							<v-col class="amber lighten-5 pr-5 pl-5">
								<v-btn elevation="0"
									small
									:color="!hasReoIssue4 ? 'primary' : 'brown lighten-4'"
									@click="showREOIssue4 = !showREOIssue4">
									<span v-if="!hasReoIssue4">Add Another Issue</span>
									<span v-else>Hide Issue #4</span>
								</v-btn>
							</v-col>
						</v-row>
					</div>
					<div v-if="hasReoIssue4" class="mb-2">
						<canned-text-field
							v-model="form.reo.reo_issue_4"
							field="reo.reo_issue_4"
							field-name="reo_issue_4"
							canned-text-name="reo_issue_1"
							:form="form"
							label="REO Issue #4" />
						<canned-text-field
							v-model="form.reo.reo_action_4"
							field="reo.reo_action_4"
							field-name="reo_action_4"
							canned-text-name="reo_action_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Action #4" />
						<canned-text-field
							v-model="form.reo.reo_cost_from_4"
							field="reo.reo_cost_from_4"
							field-name="reo_cost_from_4"
							canned-text-name="reo_cost_from_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pt-3 pb-0 pl-7"
							label="Cost From" />
						<canned-text-field
							v-model="form.reo.reo_cost_to_4"
							field="reo.reo_cost_to_4"
							field-name="reo_cost_to_4"
							canned-text-name="reo_cost_to_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pl-7"
							label="Cost To" />
						<canned-text-field
							v-model="form.reo.reo_time_to_resolve_4"
							field="reo.reo_time_to_resolve_4"
							field-name="reo_time_to_resolve_4"
							canned-text-name="reo_time_to_resolve_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Time To Resolve #4" />
						<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
							<v-col class="amber lighten-5 pr-5 pl-5">
								<v-btn elevation="0"
									small
									:color="!hasReoIssue5 ? 'primary' : 'brown lighten-4'"
									@click="showREOIssue5 = !showREOIssue5">
									<span v-if="!hasReoIssue5">Add Another Issue</span>
									<span v-else>Hide Issue #5</span>
								</v-btn>
							</v-col>
						</v-row>
					</div>
					<div v-if="hasReoIssue5" class="mb-2">
						<canned-text-field
							v-model="form.reo.reo_issue_5"
							field="reo.reo_issue_5"
							field-name="reo_issue_5"
							canned-text-name="reo_issue_1"
							:form="form"
							label="REO Issue #5" />
						<canned-text-field
							v-model="form.reo.reo_action_5"
							field="reo.reo_action_5"
							field-name="reo_action_5"
							canned-text-name="reo_action_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Action #5" />
						<canned-text-field
							v-model="form.reo.reo_cost_from_5"
							field="reo.reo_cost_from_5"
							field-name="reo_cost_from_5"
							canned-text-name="reo_cost_from_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pt-3 pb-0 pl-7"
							label="Cost From" />
						<canned-text-field
							v-model="form.reo.reo_cost_to_5"
							field="reo.reo_cost_to_5"
							field-name="reo_cost_to_5"
							canned-text-name="reo_cost_to_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pl-7"
							label="Cost To" />
						<canned-text-field
							v-model="form.reo.reo_time_to_resolve_5"
							field="reo.reo_time_to_resolve_5"
							field-name="reo_time_to_resolve_5"
							canned-text-name="reo_time_to_resolve_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Time To Resolve #5" />
						<v-row style="margin-left: -20px; margin-right: -20px;" class="mb-2">
							<v-col class="amber lighten-5 pr-5 pl-5">
								<v-btn elevation="0"
									small
									:color="!hasReoIssue6 ? 'primary' : 'brown lighten-4'"
									@click="showREOIssue6 = !showREOIssue6">
									<span v-if="!hasReoIssue6">Add Another Issue</span>
									<span v-else>Hide Issue #6</span>
								</v-btn>
							</v-col>
						</v-row>
					</div>
					<div v-if="hasReoIssue6" class="mb-2">
						<canned-text-field
							v-model="form.reo.reo_issue_6"
							field="reo.reo_issue_6"
							field-name="reo_issue_6"
							canned-text-name="reo_issue_1"
							:form="form"
							label="REO Issue #6" />
						<canned-text-field
							v-model="form.reo.reo_action_6"
							field="reo.reo_action_6"
							field-name="reo_action_6"
							canned-text-name="reo_action_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Action #6" />
						<canned-text-field
							v-model="form.reo.reo_cost_from_6"
							field="reo.reo_cost_from_6"
							field-name="reo_cost_from_6"
							canned-text-name="reo_cost_from_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pt-3 pb-0 pl-7"
							label="Cost From" />
						<canned-text-field
							v-model="form.reo.reo_cost_to_6"
							field="reo.reo_cost_to_6"
							field-name="reo_cost_to_6"
							canned-text-name="reo_cost_to_1"
							field-type="cost"
							:form="form"
							:is-editor="false"
							row-class="pl-7"
							label="Cost To" />
						<canned-text-field
							v-model="form.reo.reo_time_to_resolve_6"
							field="reo.reo_time_to_resolve_6"
							field-name="reo_time_to_resolve_6"
							canned-text-name="reo_time_to_resolve_1"
							row-class="grey lighten-5 pt-3 pb-4 form-section"
							:form="form"
							label="REO Time To Resolve #6" />
					</div>
					<canned-text-field
						v-model="form.reo.reo_general"
						field="reo.reo_general"
						field-name="reo_general"
						:form="form"
						label="Real Estate Owned Cost Speculation" />
				</v-card-text>
			</v-card>
		</v-sheet>

		<v-spacer class="py-5" />

		<images v-model="form.images" />

		<v-spacer class="py-5" />

		<v-sheet rounded class="mb-6" elevation="15">
			<v-card color="#125B92" elevation="0" class="rounded">
				<v-card-title class="align-center">
					<h3 class="white--text">Acquisition Risk Rating</h3>
					<v-spacer />
				</v-card-title>
				<v-divider />
				<v-card-text class="white pa-5">
					<v-row class="pt-4">
						<v-col cols="4">
							<v-select
								v-model="form.property_risk"
								:items="['','low','medium','high']"
								persistent-placeholder
								:error="hasErrors(form, 'property_risk')"
								:hide-details="! hasErrors(form, 'property_risk')"
								:error-messages="errorMsg(form, 'property_risk')"
								label="Site Risk"
								outlined
								dense
								class="mb-4">
								<template #selection="data">
									<v-icon v-if="data.item === 'low'" color="success" left>mdi-circle</v-icon>
									<v-icon v-if="data.item === 'medium'" color="orange" left>mdi-circle</v-icon>
									<v-icon v-if="data.item === 'high'" color="red" left>mdi-circle</v-icon>
									{{ data.item | capitalize }}
								</template>
								<template #item="data">
									<v-icon v-if="data.item === 'low'" color="success" left>mdi-circle</v-icon>
									<v-icon v-if="data.item === 'medium'" color="orange" left>mdi-circle</v-icon>
									<v-icon v-if="data.item === 'high'" color="red" left>mdi-circle</v-icon>
									{{ data.item | capitalize }}
								</template>
							</v-select>
						</v-col>
						<v-col cols="4">
							<v-select
								v-model="form.investor_risk"
								:items="['','low','medium','high']"
								persistent-placeholder
								:error="hasErrors(form, 'investor_risk')"
								:hide-details="! hasErrors(form, 'investor_risk')"
								:error-messages="errorMsg(form, 'investor_risk')"
								label="Investor Risk"
								outlined
								dense
								class="mb-4">
								<template #selection="data">
									<v-icon v-if="data.item === 'low'" color="success" left>mdi-circle</v-icon>
									<v-icon v-if="data.item === 'medium'" color="orange" left>mdi-circle</v-icon>
									<v-icon v-if="data.item === 'high'" color="red" left>mdi-circle</v-icon>
									{{ data.item | capitalize }}
								</template>
								<template #item="data">
									<v-icon v-if="data.item === 'low'" color="success" left>mdi-circle</v-icon>
									<v-icon v-if="data.item === 'medium'" color="orange" left>mdi-circle</v-icon>
									<v-icon v-if="data.item === 'high'" color="red" left>mdi-circle</v-icon>
									{{ data.item | capitalize }}
								</template>
							</v-select>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-sheet>
		<v-spacer class="py-5" />

		<v-btn
			elevation="5"
			color="success"
			dark
			fixed
			x-large
			bottom
			right
			class="floating-save-btn justify-start mb-3"
			@click="handleForm">
			<v-icon left class="pr-3 pl-1">mdi-content-save</v-icon>
			Save Data
		</v-btn>
		<v-btn
			elevation="5"
			color="warning"
			dark
			fixed
			bottom
			right
			class="floating-top-btn justify-start"
			@click="toTop">
			<v-icon left class="pr-3 pl-1">mdi-arrow-up-bold</v-icon>
			Back to top
		</v-btn>
	</page>
</template>
<script>
import api from '@/api'
import Backend from '@/Layouts/Backend'
import Page from '@/Pages/Backend/Components/Page'
import PropertySummary from './Components/PropertySummary'
import CannedTextField from './Components/CannedTextField'
import PropertyForm from '@/Pages/Backend/Properties/Form'
import Images from '@/Pages/Backend/Properties/ReportWriter/Images'

export default {
	components: { Page, PropertySummary, CannedTextField, PropertyForm, Images },
	layout: Backend,
	props: {
		property: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		project: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		client: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		buildingTypes: {
			type: [Array, Object],
			default: () => {},
			required: true
		}
	},
	data () {
		return {
			statusMenu: false,
			statusId: null,
			statuses: [],
			breadcrumbs: [
				{ route: 'dashboard', label: 'Dashboard' },
				{ route: 'clients.show', params: { client: this.client }, label: this.client.name },
				{ route: 'projects.show', params: { project: this.project }, label: this.project.name, id: this.project.id },
				{ label: this.property.full_address }
			],
			formValid: true,
			showSpecialServicing2: false,
			showSpecialServicing3: false,
			showSpecialServicing4: false,
			showSpecialServicing5: false,
			showSpecialServicing6: false,
			showREOIssue2: false,
			showREOIssue3: false,
			showREOIssue4: false,
			showREOIssue5: false,
			showREOIssue6: false,
			dialogEdit: false,
			form: this.$inertia.form({
				property_risk: null,
				investor_risk: null,
				current_site_analysis: {
					site_impact_likelihood: null,
					number_of_buildings: null,
					building_type: null,
					number_of_units: null,
					building_sq_feet: null,
					date_constructed: null,
					lot_size: null,
					current_site_usage: null,
					current_hazmat: null,
					drinking_water_source: null,
					waste_system: null,
					heating_fuel: null,
					flood_zone: null,
					current_hazmat_likelihood: null,
					current_tank_info: null,
					lbp_likelyhood: null,
					acm_likelyhood: null,
					railroad_spur: null,
					current_condition_general: null,
					longitude: null,
					latitude: null,
					current_site_analysis: null,
					current_site_floodzone: null
				},
				historic_site_analysis: {
					property_historic_development: null,
					historic_operation_in_current_facility: null,
					historical_site_use: null,
					historic_hazmat_likelihood: null,
					phase_I_report_date: null,
					phase_I_consultant: null,
					phase_I_generated_for: null,
					phase_II_report_date: null,
					phase_II_consultant: null,
					phase_II_generated_for: null,
					historic_tank_info: null,
					previous_environmental_investigation: null,
					previous_environmental_investigation_II: null
				},
				environmental_database_review: {
					pfas: null,
					other_environmental_concerns: null,
					site_environmental_database_review: null,
					offsite_environmental_database_review: null,
					miles_from_site_of_nearest_haz_release_property: null,
					risk_factors_review: null
				},
				off_site_analysis: {
					off_Site_hazmat_likelihood: null,
					north_abutter: null,
					south_abutter: null,
					east_abutter: null,
					west_abutter: null,
					current_use_of_abutting_properties: null,
					historical_use_of_abutting_properties: null,
					off_site_analysis_general: null
				},
				neb_summary: {
					known_contamination: null,
					known_contamination_reason: null,
					potential_cost_as_perc_of_upb: null,
					public_remediation_funding: null,
					neb_summary_general: null,
					likelyhood_of_contamination_reason: null,
					likely_nature_of_potential_contamination: null
				},
				executive_summary: {
					executive_summary_general: null
				},

				master_servicing: {
					master_servicing_general: '',
					issues: [
						{
							show: true,
							master_servicing_issue: '',
							master_servicing_action: '',
							master_servicing_cost_from: '',
							master_servicing_cost_to: '',
							master_servicing_time_to_resolve: ''
						}
					]
				},

				special_servicing: {
					special_servicing_general: null,
					sp_issue_1: null,
					sp_action_1: null,
					sp_cost_to_1: null,
					sp_cost_from_1: null,
					sp_time_to_resolve_1: null,
					sp_issue_2: null,
					sp_action_2: null,
					sp_cost_to_2: null,
					sp_cost_from_2: null,
					sp_time_to_resolve_2: null,
					sp_issue_3: null,
					sp_action_3: null,
					sp_cost_to_3: null,
					sp_cost_from_3: null,
					sp_time_to_resolve_3: null,
					sp_issue_4: null,
					sp_action_4: null,
					sp_cost_to_4: null,
					sp_cost_from_4: null,
					sp_time_to_resolve_4: null,
					sp_issue_5: null,
					sp_action_5: null,
					sp_cost_to_5: null,
					sp_cost_from_5: null,
					sp_time_to_resolve_5: null,
					sp_issue_6: null,
					sp_action_6: null,
					sp_cost_to_6: null,
					sp_cost_from_6: null,
					sp_time_to_resolve_6: null
				},

				reo: {
					reo_general: null,
					reo_issue_1: null,
					reo_action_1: null,
					reo_cost_from_1: null,
					reo_cost_to_1: null,
					reo_time_to_resolve_1: null,
					reo_issue_2: null,
					reo_action_2: null,
					reo_cost_from_2: null,
					reo_cost_to_2: null,
					reo_time_to_resolve_2: null,
					reo_issue_3: null,
					reo_action_3: null,
					reo_cost_from_3: null,
					reo_cost_to_3: null,
					reo_time_to_resolve_3: null,
					reo_issue_4: null,
					reo_action_4: null,
					reo_cost_from_4: null,
					reo_cost_to_4: null,
					reo_time_to_resolve_4: null,
					reo_issue_5: null,
					reo_action_5: null,
					reo_cost_from_5: null,
					reo_cost_to_5: null,
					reo_time_to_resolve_5: null,
					reo_issue_6: null,
					reo_action_6: null,
					reo_cost_from_6: null,
					reo_cost_to_6: null,
					reo_time_to_resolve_6: null
				},

				images: []
			})
		}
	},
	computed: {
		hasSpecialServicing2 () {
			return this.showSpecialServicing2 === true
			|| this.form.special_servicing.sp_issue_2
		},
		hasSpecialServicing3 () {
			return this.showSpecialServicing3 === true
				|| this.form.special_servicing.sp_issue_3
		},
		hasSpecialServicing4 () {
			return this.showSpecialServicing4 === true
				|| this.form.special_servicing.sp_issue_4
		},
		hasSpecialServicing5 () {
			return this.showSpecialServicing5 === true
				|| this.form.special_servicing.sp_issue_5
		},
		hasSpecialServicing6 () {
			return this.showSpecialServicing6 === true
				|| this.form.special_servicing.sp_issue_6
		},
		hasReoIssue2 () {
			return this.showREOIssue2 === true
			|| this.form.reo.reo_issue_2
		},
		hasReoIssue3 () {
			return this.showREOIssue3 === true
				|| this.form.reo.reo_issue_3
		},
		hasReoIssue4 () {
			return this.showREOIssue4 === true
				|| this.form.reo.reo_issue_4
		},
		hasReoIssue5 () {
			return this.showREOIssue5 === true
				|| this.form.reo.reo_issue_5
		},
		hasReoIssue6 () {
			return this.showREOIssue6 === true
				|| this.form.reo.reo_issue_6
		}
	},
	mounted () {
		this.getPropertyStatuses()
		this.populateForm()
		this.statusId = this.property.status.id
	},
	methods: {
		getPropertyStatuses () {
			const method = 'GET'
			const data = {}

			const url = this.route('property-statuses.api.index')

			return api({
				method,
				url,
				data
			}).then(response => {
				this.statuses = response.data
			})
		},
		updateStatus () {
			const data = { status_id: this.statusId }
			this.$inertia.put(this.route('properties.update.status', this.property), data, {
				only: ['property', 'flash'],
				onFinish: () => this.statusMenu = false,
				preserveState: true,
				preserveScroll: true
			})
		},
		populateForm () {
			if (! this.property) {
				return
			}

			for (const propertyKey in this.property) {
				if (! this.property.hasOwnProperty(propertyKey)) continue
				if (!this.form.hasOwnProperty(propertyKey)) continue

				// Master servicing is a proper array
				// TODO: refactor the rest one day?
				if(propertyKey === 'master_servicing') {
					if(this.length(this.property[propertyKey]['issues'])) {
						this.$set(this.form, 'master_servicing', this.property[propertyKey])
					}

					continue
				}

				if(propertyKey === 'images') {
					this.$set(this.form, propertyKey, this.property[propertyKey])

					continue
				}

				// Loop through each item inside the sub-array
				// without deleting any of the keys in the inertia form object
				if (typeof this.property[propertyKey] === 'object') {
					if (! this.property[propertyKey] || typeof this.property[propertyKey] !== 'object') continue
					const subArr = this.property[propertyKey]

					for (const subArrKey in subArr) {
						if (! this.form[propertyKey].hasOwnProperty(subArrKey)) continue
						this.$set(this.form[propertyKey], subArrKey, subArr[subArrKey])
					}

					continue
				}

				// Populate the form key
				this.$set(this.form, propertyKey, this.property[propertyKey])
			}
		},
		generateCurrentSiteAnalysis () {
			const number_of_buildings = this.form.current_site_analysis.number_of_buildings
			const building_type = this.form.current_site_analysis.building_type
			const number_of_units = this.form.current_site_analysis.number_of_units
			const building_sq_feet = this.form.current_site_analysis.building_sq_feet
			const date_constructed = this.form.current_site_analysis.date_constructed
			const lot_size = this.form.current_site_analysis.lot_size

			let sentence = number_of_buildings + ' ' +
				building_type + ' facility with ' +
				number_of_units + ' units containing ' +
				building_sq_feet + ' square feet that was constructed in ' +
				date_constructed + ' improves the ' +
				lot_size + ' acre Site. '

			tinymce.get('current_site_analysis').insertContent(sentence)
		},
		generateWaterFuelWasteFloodZone () {
			const drinking_water_source = this.form.current_site_analysis.drinking_water_source
			const waste_system = this.form.current_site_analysis.waste_system
			const heating_fuel = this.form.current_site_analysis.heating_fuel

			let sentence = '<p>The facility is connected to ' +
				drinking_water_source + ' and ' +
				waste_system + '.  The facility appears to use ' +
				heating_fuel + ' for HVAC. ' + '</p>'

			tinymce.get('current_site_floodzone').insertContent(sentence)

		},
		generateFloodZone () {
			const flood_zone = this.form.current_site_analysis.flood_zone

			let floodzone = '<p>' + 'The Site is in FEMA flood zone ' + flood_zone + '.</p>'

			tinymce.get('risk_factors_review').insertContent(floodzone)
		},
		handleForm () {
			this.form.put(this.route('properties.data.update', this.property), {
				only: ['property', 'form', 'errors', 'flash'],
				preserveScroll: true,
				preserveState: true,
				onSuccess: () => {
					this.$emit('success')
				}
			})
		},
		toTop () {
			window.scrollTo({ top: 0, behavior: 'smooth' })
		},
		addIssue (key, properties) {
			const issue = this.form[key]['issues'].find(issue => !issue.show)
			if (issue) {
				issue.show = true

				return
			}

			this.form[key]['issues'].push(
				properties.reduce((obj, key) => {
					obj[key] = ''
					return obj
				}, { show: true })
			)
		},
		hideIssue (key, index) {
			if(key === 0) {
				return
			}

			this.form[key]['issues'][index]['show'] = false
		}
	}
}
</script>
